import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { auth as authApi } from '../utils/api';
import { Shield, Key } from 'lucide-react';

export const Login = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', name: '' });

  // 2FA State
  const [twoFAStep, setTwoFAStep] = useState(null); // null | 'setup' | 'verify'
  const [tempToken, setTempToken] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [pendingUser, setPendingUser] = useState(null);
  const [resending, setResending] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await authApi.login(loginData.email, loginData.password);
      if (result.requires_2fa_setup) {
        setTempToken(result.temp_token);
        setPendingUser(result.user);
        setMaskedEmail(result.masked_email || '');
        setTwoFAStep('setup');
        toast.info('Verification code sent to your email');
      } else if (result.requires_2fa) {
        setTempToken(result.temp_token);
        setPendingUser(result.user);
        setMaskedEmail(result.masked_email || '');
        setTwoFAStep('verify');
        toast.info('Verification code sent to your email');
      } else {
        // Regular customer login
        await login(loginData.email, loginData.password);
        toast.success('Logged in successfully');
        navigate('/');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Invalid credentials';
      toast.error(msg);
    }
    setLoading(false);
  };

  const handleVerify2FASetup = async () => {
    if (twoFACode.length !== 6) { toast.error('Enter 6-digit code'); return; }
    setLoading(true);
    try {
      const result = await authApi.verify2FASetup(twoFACode, tempToken);
      toast.success('Verified! Redirecting to admin panel...');
      window.location.href = `/${result.admin_path}`;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code. Try again.');
    }
    setLoading(false);
  };

  const handleVerify2FA = async () => {
    if (twoFACode.length !== 6) { toast.error('Enter your 6-digit code'); return; }
    setLoading(true);
    try {
      const result = await authApi.verify2FA(twoFACode, tempToken);
      toast.success('Verified! Redirecting...');
      window.location.href = `/${result.admin_path}`;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    }
    setLoading(false);
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      const result = await authApi.send2FACode(tempToken);
      setMaskedEmail(result.masked_email || maskedEmail);
      toast.success('New code sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    }
    setResending(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerData.email, registerData.password, registerData.name);
      toast.success('Account created successfully');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    }
    setLoading(false);
  };

  // ═══ 2FA SETUP SCREEN (Email-based) ═══
  if (twoFAStep === 'setup') {
    return (
      <div className="min-h-screen section-padding flex items-center justify-center" data-testid="2fa-setup-page">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-[#C9A84C]/10 flex items-center justify-center">
              <Shield className="w-7 h-7 text-[#C9A84C]" />
            </div>
          </div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight mb-2 text-center" data-testid="2fa-setup-title">Email Verification</h1>
          <p className="text-sm text-center text-white/40 mb-6">
            We've sent a 6-digit verification code to <span className="text-[#C9A84C]">{maskedEmail}</span>
          </p>

          <div className="bg-[#161616] rounded-xl border border-white/[0.04] p-6 mb-4">
            <div>
              <Label className="text-xs text-white/50">Enter the 6-digit code from your email:</Label>
              <Input
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="input-flushed text-center text-2xl tracking-[0.3em] font-mono mt-2"
                maxLength={6}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleVerify2FASetup()}
                data-testid="2fa-setup-code-input"
              />
            </div>
            <div className="flex items-center justify-between mt-4">
              <p className="text-[9px] text-white/20">Code expires in 5 minutes</p>
              <button onClick={handleResendCode} disabled={resending} className="text-[10px] text-[#C9A84C] hover:text-[#C9A84C]/80" data-testid="resend-code-btn">
                {resending ? 'Sending...' : 'Resend code'}
              </button>
            </div>
          </div>

          <Button onClick={handleVerify2FASetup} className="btn-primary w-full" disabled={loading || twoFACode.length !== 6} data-testid="2fa-setup-verify-btn">
            {loading ? 'Verifying...' : 'Verify & Continue'}
          </Button>
        </div>
      </div>
    );
  }

  // ═══ 2FA VERIFICATION SCREEN (Email-based) ═══
  if (twoFAStep === 'verify') {
    return (
      <div className="min-h-screen section-padding flex items-center justify-center" data-testid="2fa-verify-page">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-[#C9A84C]/10 flex items-center justify-center">
              <Key className="w-7 h-7 text-[#C9A84C]" />
            </div>
          </div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight mb-2 text-center" data-testid="2fa-verify-title">Email Verification</h1>
          <p className="text-sm text-center text-white/40 mb-6">
            We've sent a 6-digit code to <span className="text-[#C9A84C]">{maskedEmail}</span>
          </p>

          <div className="bg-[#161616] rounded-xl border border-white/[0.04] p-6 mb-4">
            <Input
              value={twoFACode}
              onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="input-flushed text-center text-2xl tracking-[0.3em] font-mono"
              maxLength={6}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleVerify2FA()}
              data-testid="2fa-verify-code-input"
            />
            <div className="flex items-center justify-between mt-4">
              <p className="text-[9px] text-white/20">Code expires in 5 minutes</p>
              <button onClick={handleResendCode} disabled={resending} className="text-[10px] text-[#C9A84C] hover:text-[#C9A84C]/80" data-testid="resend-code-btn">
                {resending ? 'Sending...' : 'Resend code'}
              </button>
            </div>
          </div>

          <Button onClick={handleVerify2FA} className="btn-primary w-full" disabled={loading || twoFACode.length !== 6} data-testid="2fa-verify-submit-btn">
            {loading ? 'Verifying...' : 'Verify & Login'}
          </Button>

          <button onClick={() => { setTwoFAStep(null); setTwoFACode(''); setTempToken(''); }} className="w-full text-center text-xs text-white/30 hover:text-white/50 mt-4" data-testid="2fa-back-btn">
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // ═══ REGULAR LOGIN/REGISTER ═══
  return (
    <div className="min-h-screen section-padding flex items-center justify-center" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-5">
          <div className="relative">
            <div className="absolute -inset-2.5 bg-[#C9A84C]/10 rounded-full blur-md" />
            <div className="absolute -inset-0.5 rounded-full ring-2 ring-[#C9A84C]/15" />
            <img
              src="https://customer-assets.emergentagent.com/job_654bd78e-5e39-4822-a480-29d8c14fa990/artifacts/ow0h2qv9_Black%20and%20White%20Minimalist%20Round%20Beauty%20And%20Spa%20Logo.png"
              alt="Vintage Wardrobe Co"
              className="relative w-20 h-20 rounded-full object-cover shadow-lg bg-white"
              data-testid="login-logo"
            />
          </div>
        </div>
        <h1 className="text-4xl font-heading font-semibold tracking-tight mb-8 text-center" data-testid="login-title">Account</h1>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8" data-testid="auth-tabs">
            <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
            <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
          </TabsList>

          <TabsContent value="login" data-testid="login-form">
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="login-email">Email</Label>
                <Input id="login-email" type="email" value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} required className="input-flushed" data-testid="login-email-input" />
              </div>
              <div>
                <Label htmlFor="login-password">Password</Label>
                <Input id="login-password" type="password" value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} required className="input-flushed" data-testid="login-password-input" />
              </div>
              <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="login-submit-button">
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register" data-testid="register-form">
            <form onSubmit={handleRegister} className="space-y-6">
              <div>
                <Label htmlFor="register-name">Name</Label>
                <Input id="register-name" type="text" value={registerData.name} onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })} className="input-flushed" data-testid="register-name-input" />
              </div>
              <div>
                <Label htmlFor="register-email">Email</Label>
                <Input id="register-email" type="email" value={registerData.email} onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })} required className="input-flushed" data-testid="register-email-input" />
              </div>
              <div>
                <Label htmlFor="register-password">Password</Label>
                <Input id="register-password" type="password" value={registerData.password} onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })} required className="input-flushed" data-testid="register-password-input" />
              </div>
              <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="register-submit-button">
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
              <p className="text-[11px] text-center text-softgrey leading-relaxed mt-3" data-testid="register-policy-notice">
                By creating an account, you agree to our{' '}
                <Link to="/privacy-policy" className="text-[#C9A84C] hover:underline font-semibold">Privacy Policy</Link>
                {' '}and{' '}
                <Link to="/terms-conditions" className="text-[#C9A84C] hover:underline font-semibold">Terms & Conditions</Link>.
              </p>
            </form>
          </TabsContent>
        </Tabs>

        <div className="mt-8 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-home-link">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};
